import { type FC, useMemo } from 'react';
import { DragOverlay, type Modifiers, useDndContext } from '@dnd-kit/core';
import cn from 'classnames';
import { Badge } from '@direct-frontend/components';
import { type ColorlessIconProps } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { isDroppableOverDataNode } from '../../lib/isDroppableOverData';
import { isTreeRow } from '../../lib/isTreeRow';
import { type NodeTemplateRender } from '../../lib/NodeTemplateRender';
import { type TreeRow } from '../../lib/types';
import { DraggableRowOverlay } from '../DraggableRow';
import styles from './DraggingRowsOverlay.module.css';

export interface DraggingRowsOverlayProps<Attributes = unknown> extends TestProps {
    activeNode: TreeRow<Attributes>;
    renderNode: NodeTemplateRender<Attributes>;
    size: 's' | 'm';
    settings?: {
        withDefaultWidth?: boolean;
        defaultItemHeight?: number;
        dragIcon?: React.ReactNode;
        withDragCounter?: boolean;
    };
    IconDnd?: FC<ColorlessIconProps>;
    iconSize?: ColorlessIconProps['size'];
}

const customModifiers: Modifiers = [
    (args) => {
        const { activeNodeRect, scrollableAncestorRects, scrollableAncestors, transform } = args;

        if (!activeNodeRect) {
            return transform;
        }

        const index = scrollableAncestors.findIndex((el) => el.hasAttribute('data-dnd-viewport'));

        if (index < 0) {
            return transform;
        }

        const scrollableAncestorRect = scrollableAncestorRects[index];

        if (!scrollableAncestorRect) {
            return transform;
        }

        return {
            ...transform,
            x: scrollableAncestorRect.left - activeNodeRect.left,
        };
    },
];

export const DraggingRowsOverlay = <Attributes = unknown,>({
    'data-testid': dataTestId = 'DraggingRowsOverlay',
    activeNode,
    renderNode,
    size,
    settings,
    IconDnd,
    iconSize,
}: DraggingRowsOverlayProps<Attributes>) => {
    const { over } = useDndContext();
    const overData = over?.data.current;

    const overlayContent = useMemo(() => {
        if (!isTreeRow<Attributes>(activeNode)) {
            return;
        }

        return (
            <div
                className={cn(styles.container, {
                    [styles.dropIn]: isDroppableOverDataNode(overData) && overData.node.id !== activeNode.id,
                })}
            >
                <DraggableRowOverlay<Attributes>
                    size={size}
                    key={activeNode.id}
                    node={activeNode}
                    insertAsChild={overData?.type === 'node'}
                    overNode={overData?.node}
                    isExpanded={false}
                    renderNode={renderNode}
                    settings={settings}
                    IconDnd={IconDnd}
                    iconSize={iconSize}
                />
                {activeNode.childrenIds.size > 0 && settings?.withDragCounter !== false ? (
                    <Badge className={styles.badge} size="24" color="blue">
                        {activeNode.childrenIds.size + 1}
                    </Badge>
                ) : null}
            </div>
        );
    }, [activeNode, overData, size, renderNode, settings, IconDnd, iconSize]);

    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Автоматический игнор в рамках DIRECT-288723
    const customStyle = useMemo(() => {
        let style = {};

        if (!settings?.withDefaultWidth) {
            style = {
                ...style,
                width: 500,
            };
        }

        if (settings?.defaultItemHeight) {
            style = {
                ...style,
                height: settings.defaultItemHeight,
            };
        }

        return style;
    }, [settings?.withDefaultWidth, settings?.defaultItemHeight]);

    return (
        <DragOverlay data-testid={dataTestId} style={customStyle} modifiers={customModifiers}>
            {overlayContent}
        </DragOverlay>
    );
};
